import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { assemblySchema } from '@/assembly/api/types';
import { z } from 'zod';

/**
 * Schema for the register assembly request.
 */
const _registerRequestSchema = z.object({
  serial: z.string(),
  description: z.string().nullable(),
  metadata: z.array(
    z.object({
      fieldId: z.string(),
      value: z.union([z.string(), z.number(), z.boolean()]).nullable(),
    })
  ),
  parts: z.array(
    z.object({
      partId: z.string().uuid(),
      serials: z.array(z.string()),
    })
  ),
});

/**
 * Type for the register assembly request.
 */
export type RegisterAssemblyRequest = z.infer<typeof _registerRequestSchema>;

/**
 * Schema for the register assembly response.
 */
const registerAssemblyResponseSchema = z.object({
  data: assemblySchema,
});

/**
 * Type for the register assembly response.
 */
type RegisterAssemblyResponse = z.infer<typeof registerAssemblyResponseSchema>;

/**
 * Register an assembly.
 * @param assemblyKey - the assembly key
 * @param request The assembly data
 * @returns Promise resolving to the registered assembly
 */
export async function registerAssembly(
  assemblyKey: string,
  request: RegisterAssemblyRequest
): Promise<RegisterAssemblyResponse> {
  const url = `/v1/assembly/${assemblyKey}/register`;
  const response = await api.post(url, request);
  return parseResponse(registerAssemblyResponseSchema, response.data, {
    schemaName: 'RegisterAssembly',
    endpoint: url,
  });
}
